<script setup lang="ts">
    import { computed, inject, ref, watch } from 'vue';
    import { type ImageLibraryProvide, imageLibraryProvideName } from '@shared/components/Images/types';
    import { vTooltip } from '@shared/directives';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';

    const props = defineProps<{
        imageUrls: string[];
        modelValue: string | null;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', newValue: string | null): void;
    }>();

    const imageLibrary = inject(imageLibraryProvideName) as ImageLibraryProvide;

    // Like the old Knockout picker: a selected image that isn't among the suggestions (e.g. picked from the library) is appended.
    const images = computed(() => {
        const urls = props.imageUrls.filter((url) => !!url);
        if (props.modelValue && !urls.includes(props.modelValue)) urls.push(props.modelValue);
        return urls;
    });

    const activeIndex = ref(0);

    // Show the selected image when it changes, otherwise keep the current position within bounds.
    watch(
        () => [props.modelValue, images.value.length] as const,
        ([selected]) => {
            const index = selected ? images.value.indexOf(selected) : -1;
            activeIndex.value = index !== -1 ? index : Math.min(activeIndex.value, Math.max(images.value.length - 1, 0));
        },
        { immediate: true }
    );

    const activeUrl = computed(() => images.value[activeIndex.value] ?? null);
    const isSelected = computed(() => !!activeUrl.value && activeUrl.value === props.modelValue);

    function step(delta: number) {
        const count = images.value.length;
        activeIndex.value = (activeIndex.value + delta + count) % count;
    }

    function toggleSelected() {
        if (props.disabled || !activeUrl.value) return;
        emit('update:modelValue', isSelected.value ? null : activeUrl.value);
    }

    function openImageLibrary() {
        if (props.disabled) return;
        imageLibrary.newSrc(props.modelValue, (newValue: string) => emit('update:modelValue', newValue || null));
    }
</script>

<template>
    <div class="form-group m-sm-t">
        <div
            v-if="activeUrl"
            class="img-thumbnail img-preview d-flex align-items-center p-relative"
            :class="{ pointer: !disabled }"
            v-tooltip.top="isSelected ? '' : $Resources.SocialShareClickToSelectTooltip"
            @click="toggleSelected">
            <img class="img-responsive" :src="activeUrl" :alt="$Resources.ImagePreviewAltText" />
            <button
                type="button"
                class="image-picker-check p-absolute"
                :class="{ selected: isSelected }"
                :aria-pressed="isSelected"
                :disabled="disabled"
                @click.stop="toggleSelected">
                <svg width="30px" height="30px" viewBox="0 0 16 16" class="d-block" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        d="M10.97 4.97a.75.75 0 0 1 1.071 1.05l-3.992 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.236.236 0 0 1 .02-.022z" />
                </svg>
                <span class="sr-only">{{ $Resources.ChooseImage }}</span>
            </button>
        </div>

        <div class="btn-toolbar" role="toolbar">
            <div v-if="images.length > 1" class="btn-group" role="group">
                <button type="button" class="btn btn-default btn-sm" @click="step(-1)">
                    {{ $Resources.SocialShareSettingsImagePickerPreviousBtn }}
                </button>
                <button type="button" class="btn btn-default btn-sm" @click="step(1)">
                    {{ $Resources.SocialShareSettingsImagePickerNextBtn }}
                </button>
            </div>
            <div class="btn-group" role="group">
                <button type="button" class="btn btn-default btn-sm" :disabled="disabled" @click="openImageLibrary">
                    {{ $Resources.SocialShareSettingsImageLibraryBtn }}
                </button>
            </div>
        </div>

        <BsAlert
            v-if="!images.length"
            :type="AlertTypes.Info"
            :title="$Resources.SocialShareSettingsFoundNoImagesAlertHeading"
            :content="$Resources.SocialShareSettingsFoundNoImagesAlertBody"
            :dismissible="false"
            class="m-0-b" />
    </div>
</template>

<style>
    .image-picker-check {
        border: 0;
        padding: 0;
        top: 10px;
        left: 10px;
        background-color: #999;
        border-radius: var(--border-radius-base);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
        transition: background-color var(--transition-duration-normal) ease-in-out;
    }

    .image-picker-check.selected {
        background-color: hsl(var(--brand-primary-hue-sat), var(--brand-primary-light));
    }
</style>
